import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { financeService } from '../../services/financeService';
import { regionAssociationService } from '../../services/regionAssociationService';
import { TANZANIA_REGION_OPTIONS, getRegionLabel } from '../../utils/tanzaniaRegions';
import { getBankOptions } from '../../data/tanzaniaBanks';

const EMPTY_FORM = {
  financeContactName: '',
  financeContactEmail: '',
  financeContactPhone: '',
  bankName: '',
  bankBranch: '',
  accountName: '',
  accountNumber: '',
  swiftCode: '',
  mobileMoneyProvider: '',
  mobileMoneyNumber: '',
  taxIdentificationNumber: '',
  notes: '',
};

const RegionFinance = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [profileData, associationData] = await Promise.all([
        financeService.getRegionFinanceProfiles(),
        regionAssociationService.getAll().catch(() => []),
      ]);
      setProfiles(profileData);
      setAssociations(associationData || []);
      if (!selectedRegion && TANZANIA_REGION_OPTIONS.length) {
        setSelectedRegion(TANZANIA_REGION_OPTIONS[0].value);
      }
    } catch (err) {
      setError(err.message || 'Failed to load region finance data');
    } finally {
      setIsLoading(false);
    }
  }, [selectedRegion]);

  useEffect(() => {
    loadData();
  }, []);

  const profileMap = useMemo(() => {
    const map = {};
    profiles.forEach((p) => { map[p.region] = p; });
    return map;
  }, [profiles]);

  const associationMap = useMemo(() => {
    const map = {};
    associations.forEach((a) => { map[a.region] = a; });
    return map;
  }, [associations]);

  useEffect(() => {
    if (!selectedRegion) return;
    const existing = profileMap[selectedRegion];
    setForm(existing ? {
      financeContactName: existing.financeContactName || '',
      financeContactEmail: existing.financeContactEmail || '',
      financeContactPhone: existing.financeContactPhone || '',
      bankName: existing.bankName || '',
      bankBranch: existing.bankBranch || '',
      accountName: existing.accountName || '',
      accountNumber: existing.accountNumber || '',
      swiftCode: existing.swiftCode || '',
      mobileMoneyProvider: existing.mobileMoneyProvider || '',
      mobileMoneyNumber: existing.mobileMoneyNumber || '',
      taxIdentificationNumber: existing.taxIdentificationNumber || '',
      notes: existing.notes || '',
    } : EMPTY_FORM);
  }, [selectedRegion, profileMap]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setSuccess(null);
  };

  const handleSave = async () => {
    if (!selectedRegion) return;
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const saved = await financeService.upsertRegionFinanceProfile({
        region: selectedRegion,
        ...form,
      });
      setProfiles((prev) => {
        const others = prev.filter((p) => p.region !== selectedRegion);
        return [...others, saved].sort((a, b) => a.region.localeCompare(b.region));
      });
      setSuccess(`Finance profile saved for ${getRegionLabel(selectedRegion)}.`);
    } catch (err) {
      setError(err.message || 'Failed to save region finance profile');
    } finally {
      setIsSaving(false);
    }
  };

  const selectedAssociation = associationMap[selectedRegion];

  const bankOptions = useMemo(
    () => getBankOptions(form.bankName),
    [form.bankName]
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'}`}>
        <div className="p-6 space-y-6 max-w-5xl mx-auto">
          <Breadcrumb items={[
            { label: 'Finance', path: '/finance-dashboard' },
            { label: 'Region Finance Profiles' },
          ]} />

          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="MapPin" size={24} className="text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Region Finance Profiles</h1>
              <p className="text-muted-foreground">Manage bank details and finance contacts per region</p>
            </div>
          </div>

          {error && (
            <div className="p-4 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive text-sm">{error}</div>
          )}
          {success && (
            <div className="p-4 rounded-lg border bg-success/10 border-success/20 text-success text-sm">{success}</div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-card border border-border rounded-lg p-4 card-shadow space-y-2 max-h-[32rem] overflow-y-auto">
              <h3 className="text-sm font-semibold text-foreground mb-2">Regions</h3>
              {TANZANIA_REGION_OPTIONS.map((region) => {
                const configured = !!(profileMap[region.value]?.bankName || profileMap[region.value]?.accountNumber);
                return (
                  <button
                    key={region.value}
                    type="button"
                    onClick={() => setSelectedRegion(region.value)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                      selectedRegion === region.value
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'hover:bg-muted/60 text-foreground'
                    }`}
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span>{region.label}</span>
                      {configured && <Icon name="CheckCircle2" size={14} className="text-success shrink-0" />}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="lg:col-span-2 bg-card border border-border rounded-lg p-6 card-shadow space-y-6">
              {isLoading ? (
                <div className="flex justify-center py-12">
                  <Icon name="Loader2" size={32} className="animate-spin text-primary" />
                </div>
              ) : (
                <>
                  <div>
                    <Select
                      label="Region"
                      options={TANZANIA_REGION_OPTIONS}
                      value={selectedRegion}
                      onChange={setSelectedRegion}
                    />
                    {selectedAssociation && (
                      <div className="mt-3 p-3 bg-muted/30 rounded-lg border border-border text-sm">
                        <p className="font-medium text-foreground">{selectedAssociation.associationName}</p>
                        <p className="text-muted-foreground">{selectedAssociation.email || 'No association email'}</p>
                        <p className="text-muted-foreground capitalize">Status: {selectedAssociation.registrationStatus}</p>
                      </div>
                    )}
                  </div>

                  <section className="space-y-4">
                    <h3 className="text-sm font-semibold border-b border-border pb-2">Finance Contact</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input label="Contact Name" value={form.financeContactName} onChange={(e) => handleChange('financeContactName', e.target.value)} />
                      <Input label="Contact Email" type="email" value={form.financeContactEmail} onChange={(e) => handleChange('financeContactEmail', e.target.value)} />
                      <Input label="Contact Phone" value={form.financeContactPhone} onChange={(e) => handleChange('financeContactPhone', e.target.value)} />
                      <Input label="TIN / Tax ID" value={form.taxIdentificationNumber} onChange={(e) => handleChange('taxIdentificationNumber', e.target.value)} />
                    </div>
                  </section>

                  <section className="space-y-4">
                    <h3 className="text-sm font-semibold border-b border-border pb-2">Bank Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Select
                        label="Bank Name"
                        placeholder="Select bank"
                        options={bankOptions}
                        value={form.bankName}
                        onChange={(value) => handleChange('bankName', value)}
                        searchable
                        clearable
                      />
                      <Input label="Branch" value={form.bankBranch} onChange={(e) => handleChange('bankBranch', e.target.value)} />
                      <Input label="Account Name" value={form.accountName} onChange={(e) => handleChange('accountName', e.target.value)} />
                      <Input label="Account Number" value={form.accountNumber} onChange={(e) => handleChange('accountNumber', e.target.value)} />
                      <Input label="SWIFT / BIC Code" value={form.swiftCode} onChange={(e) => handleChange('swiftCode', e.target.value)} />
                    </div>
                  </section>

                  <section className="space-y-4">
                    <h3 className="text-sm font-semibold border-b border-border pb-2">Mobile Money</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input label="Provider" value={form.mobileMoneyProvider} onChange={(e) => handleChange('mobileMoneyProvider', e.target.value)} placeholder="M-Pesa, Tigo Pesa, etc." />
                      <Input label="Mobile Money Number" value={form.mobileMoneyNumber} onChange={(e) => handleChange('mobileMoneyNumber', e.target.value)} />
                    </div>
                  </section>

                  <Input label="Notes" value={form.notes} onChange={(e) => handleChange('notes', e.target.value)} />

                  <div className="flex justify-end">
                    <Button loading={isSaving} iconName="Save" onClick={handleSave}>
                      Save Region Profile
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RegionFinance;
